import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../Config/axiosInstance';
import AddExternalResourceInvoice from './AddExternalResourceInvoice';
import EditExternalResourceInvoice from './EditExternalResourceInvoice';
import PaymentExternalResourceInvoice from './PaymentExternalResourceInvoice';
import './ExternalResourceInvoice.css';
import { Box } from '@mui/material';
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function ExternalResourceInvoice() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canViewPayment, setCanViewPayment] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  //Functie de formatare suma
  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  //Permisiuni
  useEffect(() => {
    const requiredPath = "/external-resource-invoice";
    const requiredPathPayment = "/external-resource-invoice/payment-external-resource-invoice";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_create));
    setCanEdit(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_edit));
    setCanViewPayment(permissions.some((page) => new URL(page.url).pathname === requiredPathPayment && page.can_view));
    setCanDelete(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_delete));
  }, []);

  //Fech Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          invoiceResponse,
          suppliersResponse,
          costResponse,
          codesResponse,
          projectBudget,
          currenciesResponse,
          exchangeRatesResponse,
          systemCurrencyResponse,
        ] = await Promise.all([
          axiosInstance.get('/external-resource-invoice/'),
          axiosInstance.get('/suppliers/'),
          axiosInstance.get('/budget-structure/'),
          axiosInstance.get('/project-codes/'),
          axiosInstance.get('/project-budget/'),
          axiosInstance.get('/currencies/'),
          axiosInstance.get('/exchange-rate/'),
          axiosInstance.get('/system-currency/'),
        ]);

        const codeMap = codesResponse.data.reduce((acc, code) => {
          acc[code.id] = code.code;
          return acc;
        }, {});

        const supplierMap = suppliersResponse.data.reduce((acc, supplier) => {
          acc[supplier.id] = supplier.name;
          return acc;
        }, {});

        const costMap = costResponse.data.reduce((acc, cost) => {
          acc[cost.id] = cost.cost_element_description;
          return acc;
        }, {});

        const PBtCostDescriptionMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const currencyMap = currenciesResponse.data.reduce((acc, currency) => {
          acc[currency.id] = currency.abbreviation || currency.name;
          return acc;
        }, {});

        const exchangeRateMap = exchangeRatesResponse.data.reduce(
          (acc, rate) => {
            acc[rate.id] = rate.value;
            return acc;
          },
          {}
        );

        const systemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) &&
          systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency
            : null;

        const systemCurrencyLabel =
          currencyMap[systemCurrencyId] || "System";

        const sortedData = invoiceResponse.data.map((item) => {
          const currencyLabel = currencyMap[item.currency] || "-";
          const exchangeRateValue =
            item.exchange_rate_value ||
            exchangeRateMap[item.exchange_rate] ||
            "";

          return {
            id: item.id,
            invoice_number: item.invoice_number || "N/A",
            project_code: codeMap[item.project_code] || "Unknown",
            cost_id: costMap[PBtCostDescriptionMap[item.cost_id]] || "Not Available",
            invoice_without_vat: item.invoice_without_vat || 0,
            total_invoice_with_vat: item.total_invoice_with_vat || 0,
            currency: item.currency || "",
            currency_label: currencyLabel,
            exchange_rate: item.exchange_rate || "",
            exchange_rate_value: exchangeRateValue,
            invoice_without_vat_system:
              item.invoice_without_vat_system || "",
            total_invoice_with_vat_system:
              item.total_invoice_with_vat_system || "",
            system_currency_label: systemCurrencyLabel,
            bill_maturity: item.bill_maturity,
            supplier: supplierMap[item.supplier] || "Unknown",
            payments_status: item.payments_status
          };
        }).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  //Navigare add si edit
  const handleAddClick = () => navigate('/external-resource-invoice/add-external-resource-invoice');
  const handleEditClick = (row) => navigate(`/external-resource-invoice/edit-external-resource-invoice/${row.id}`);

  //Deschide dialog delete
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  //Confirma stergerea
  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/external-resource-invoice/${itemToDelete.id}/`);
      setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting invoice:', error.response?.data || error.message);
    }
  };

  //Culori statusuri
  const statusColors = {
    "Partially paid": '#ff9800',
    Paid: '#4caf50',
    Unpaid: '#f44336',
  };

  //Coloane
  const columns = [
    {
      field: 'invoice_number',
      headerName: 'Invoice Number',
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'project_code',
      headerName: 'Project Code',
      flex: 1.8,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'cost_id',
      headerName: 'Cost Description',
      flex: 1.8,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'invoice_without_vat',
      headerName: 'Invoice without VAT',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatNumber(params.value)} {params.row.currency_label || ""}
        </div>
      )
    },
    {
      field: 'total_invoice_with_vat',
      headerName: 'Total invoice with VAT',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatNumber(params.value)} {params.row.currency_label || ""}
        </div>
      )
    },
    {
      field: "invoice_without_vat_system",
      headerName: "Without VAT System",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value
            ? `${formatNumber(params.value)} ${
                params.row.system_currency_label || ""
              }`
            : "-"}
        </div>
      ),
    },
    {
      field: "total_invoice_with_vat_system",
      headerName: "With VAT System",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value
            ? `${formatNumber(params.value)} ${
                params.row.system_currency_label || ""
              }`
            : "-"}
        </div>
      ),
    },
    {
      field: "bill_maturity",
      headerName: "Bill Maturity",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const date = params.value ? new Date(params.value) : null;
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {date ? date.toLocaleDateString("ro-RO") : "–"}
          </div>
        );
      }
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: 1.3,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'payments_status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span
            style={{
              backgroundColor: statusColors[params.value] || '#ccc',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '10px',
              width: '90px',
              maxWidth: '90px',
              minWidth: '90px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '15px'
            }}
          >
            {params.value}
          </span>
        </div>
      )
    },
  ];

  if (canEdit || canViewPayment || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 150,
      minWidth: 150,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}

          {canViewPayment && (
            <IconButton onClick={() => navigate(`/external-resource-invoice/payment-external-resource-invoice/${params.row.id}`)}>
              <VisibilityIcon sx={{ color: '#1976d2' }} />
            </IconButton>
          )}

          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="external-resource-page">
          <div className="external-resource-container">
            <h2 className='title'>EXTERNAL RESOURCE INVOICE</h2>
            {canCreate && (
              <div className="add-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{ float: 'right', marginBottom: '40px' }}
                >
                  Add Invoice
                </Button>
              </div>
            )}
          </div>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete invoice {itemToDelete?.invoice_number}?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#bdbdbd',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  }
                }}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <div className="table-container">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </div>
      } />
      <Route path="add-external-resource-invoice" element={<AddExternalResourceInvoice />} />
      <Route path="edit-external-resource-invoice/:id" element={<EditExternalResourceInvoice />} />
      <Route path="payment-external-resource-invoice/:id" element={<PaymentExternalResourceInvoice />} />
    </Routes>
  );
}

export default ExternalResourceInvoice;